
import { Controller, useForm } from "react-hook-form";
import { ClockLoader } from "react-spinners";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../components/button";
import Input from "../../components/input";
import SideImage from "../../assets/images/sideImage.png";
import AuthLayout from "../../components/layouts/auth-layout";
import { toast } from "sonner";
import useRequest from "../../components/hook/use-req";
import { PasswordLockIcon, ReloadIcon } from "../../assets/icon";
import { useState } from "react";
import PasswordCriteria from "../../components/password-criteria";

const ResetPassword = () => {
  const { handleSubmit, control, reset, watch } = useForm();
  const { loading, makeRequest } = useRequest("/auth/reset-password", "POST");
  const [viewPassword, setViewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const code = localStorage.getItem("resetOtpCode");
  const email = localStorage.getItem("resetEmail");
  const passwordValue = watch("password");
  const navigate = useNavigate();
  const handleResetPassword = handleSubmit(async (formData) => {
    const resetPassword = {
      new_password: formData.password,
      email,
      code
    };
    const [response] = await makeRequest(resetPassword);
    if (response) {
      toast.success(response.message);
      localStorage.removeItem("resetOtpCode");
      localStorage.removeItem("resetEmail");
      navigate("/login");
      reset();
    } else {
      toast.error(response?.message || "An error occurred, please try again");
    }
  });

  const handleShowPassword = () => setViewPassword((prev) => !prev);

  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  return (
    <AuthLayout sideImageSrc={SideImage}>
      <div className="mb-32">
        <h1 className="text-[32px] font-bold text-[#3A3428] dark:text-gray-100">
          Welcome to <span className="font-medium">ConvoHub</span>
        </h1>
        <p className="text-[16px] text-[#434343] dark:text-gray-400">
          Reach customers on every channel, effortlessly.
        </p>
      </div>
      <form onSubmit={handleResetPassword} className="w-full">
        <h6 className="text-[24px] font-medium text-[#3A3428] dark:text-gray-100">Create New Password</h6>
        <span className="text-[14px] text-[#434343] dark:text-gray-400 mt-2 block">
          Enter a strong password for your account
        </span>


        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            pattern: {
              value: /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
              message: "Password must be at least 8 characters long, include a number and a special character",
            },
          }}
          render={({ field, fieldState }) => (
            <div className="w-full relative">
              <Input
                label="Password"
                icon={<PasswordLockIcon />}
                type={viewPassword ? "text" : "password"}
                value={field.value}
                placeholder="Enter a Password"
                className="w-full mb-4"
                onChange={field.onChange}
              />
              <button
                onClick={handleShowPassword}
                onMouseDown={handleMouseDownPassword}
                className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                type="button"
              >
                {!viewPassword ? "Show" : "Close"}
              </button>
              {passwordValue?.length > 0 && (
                <PasswordCriteria password={passwordValue} />
              )}
              {fieldState?.error && (
                <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        <div className='mt-7'>
          <Controller
            name="confirm_password"
            control={control}
            rules={{
              required: "Confirm password is required",
              validate: (value) =>
                value === watch("password") || "Passwords do not match",
            }}
            render={({ field, fieldState }) => (
              <div className="w-full relative">
                <Input
                  label="Confirm Password"
                  icon={<ReloadIcon />}
                  type={showConfirmPassword ? "text" : "password"}
                  value={field.value}
                  placeholder="Re-enter Password"
                  className="w-full mb-4"
                  onChange={field.onChange}
                />
                <button
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                  type="button"
                >
                  {!showConfirmPassword ? "Show" : "Close"}
                </button>
                {fieldState?.error && (
                  <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>
        <Button
          size="lg"
          variant="primary"
          className="w-full mt-5"
          type="submit"
        >
          {loading ? (
            <ClockLoader color="#ffffff" loading={loading} size={20} />
          ) : (
            "Submit"
          )}

        </Button>
        <p className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400">
          Remember your Password?{" "}
          <Link to="/login" className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors">
            Sign In
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
